import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { AiMessageSourceRoute } from '@/config/router/routes';
import { useChatController } from '@/presentation/providers/chatController/chatControllerContext';
import type { AssistantMessage } from '@/presentation/providers/chatController/chatMessage';
import type { ChatValue } from '@/presentation/providers/chatController/chatValue';
import {
  PrimaryInputField,
  useTextController,
} from '@/presentation/widgets/PrimaryInputField';
import { SafeArea } from '@/presentation/widgets/SafeArea';

import { ChatMessageTile } from './ChatMessageTile';
import styles from './ChatView.module.css';

/**
 * Перенос `chat_page/chat_view.dart` — лента сообщений.
 *
 * Чего не стало и почему: в Dart вокруг списка стояли `LayoutBuilder` +
 * `OverflowBox` и отступ снизу на `difference` — разницу высот до и после
 * появления поля ввода. Приём удерживал ленту на всю высоту экрана и не
 * давал последнему сообщению уехать под клавиатуру. В браузере ту же
 * работу делает визуальный вьюпорт: он сжимается под клавиатуру, за ним
 * следует `--screen-height`, а лента, выстроенная снизу вверх
 * (`flex-direction: column-reverse` + прокрутка), остаётся прижатой к полю
 * ввода. Смысл сохранён: последнее сообщение стоит там же, где стояло.
 */

export interface ChatViewProps {
  readonly chatValue: ChatValue;
  /** `ValueNotifier<int?> selectedMessage` — значение и способ его сменить. */
  readonly selectedMessage: number | null;
  readonly onSelectMessage: (id: number | null) => void;
}

export function ChatView(props: ChatViewProps): ReactElement {
  const { chatValue, selectedMessage, onSelectMessage } = props;
  const chatController = useChatController();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // `final TextEditingController _controller = TextEditingController()`
  const textController = useTextController();
  const messages = chatValue.messages;

  const sourceLocation = AiMessageSourceRoute.location({
    lang: chatValue.language,
    personId: chatValue.person.id,
  });

  /*
    В Dart выделение снималось после `await ...push(context)` — go_router
    возвращал управление, когда страница источника закрывалась. React
    Router ничего не ждёт, поэтому возврат определяется по адресу: были на
    оверлее, теперь нет — значит, он закрылся. Именно «были, а теперь нет»,
    а не просто «сейчас не там»: переход и выделение приходят одной
    пачкой обновлений, и порядок внутри неё не гарантирован.

    Проверка `mounted` из Dart не нужна: размонтированный экран сюда
    не попадает.
  */
  const wasOnSourceRef = useRef(false);
  useEffect(() => {
    if (pathname === sourceLocation) {
      wasOnSourceRef.current = true;
      return;
    }
    if (!wasOnSourceRef.current) return;
    wasOnSourceRef.current = false;
    onSelectMessage(null);
  }, [pathname, sourceLocation, onSelectMessage]);

  const handleSourcePressed = (hero: string, message: AssistantMessage): void => {
    const current = chatController.value;
    onSelectMessage(message.id);
    AiMessageSourceRoute.push(navigate, {
      hero,
      message,
      lang: current.language,
      personId: current.person.id,
    });
  };

  /** `_controllerListener`: любое изменение текста продлевает сеанс. */
  const handleChange = (value: string): void => {
    textController.setValue(value);
    chatController.chatDurationController.restartTimer();
  };

  const handleSubmitted = (value: string): void => {
    chatController.disableKeyboardMode();
    textController.clear();
    void chatController.addTextMessage(value);
  };

  return (
    <div className={styles.root}>
      <div className={styles.scroller}>
        <div className={styles.list}>
          {messages.map((message) => (
            <ChatMessageTile
              key={message.id}
              message={message}
              selectedMessage={selectedMessage}
              onSourcePressed={handleSourcePressed}
            />
          ))}
        </div>
      </div>
      {chatValue.keyboardMode && (
        <SafeArea top={false} left={false} right={false}>
          <div className={styles.inputPadding}>
            <PrimaryInputField
              autoFocus
              value={textController.value}
              inputRef={textController.ref}
              onChange={handleChange}
              onFocusChanged={(hasFocus) => {
                if (!hasFocus) chatController.disableKeyboardMode();
              }}
              onFieldSubmitted={handleSubmitted}
            />
          </div>
        </SafeArea>
      )}
    </div>
  );
}
